import DashboardLayout from "lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { ListGroup } from "react-bootstrap";
import { Container, Row, Col, Card, Form, Table, Alert, Button } from 'react-bootstrap';
import { AppContext } from "lib/contexts/app-context";
import { useState, useRef, useEffect, useContext } from "react";
import SignatureCanvas from "react-signature-canvas";
import usePatients from "lib/hooks/usePatients";
import { useNavigate, useLocation } from "react-router-dom";
import { isPatientUser } from "lib/utils/util";

const CryoChamberFormPage = () => {

  const { appState } = useContext(AppContext);

  const breadcrumbItems = [
    {
      label: "Dashboard",
      path: isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.Dashboard : PAGE_ROUTES.ADMIN.Dashboard,
    },
    {
      label: isPatientUser(appState?.userRole) ? "Consent Forms" : "Patients Management",
      path: isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.ConsentForm : PAGE_ROUTES.ADMIN.PatientsManagement,
    },
    {
      label: "Cryo Chamber Form",
      path: ''
    },
  ] as BreadcrumbItem[];

  const navigateTo = useNavigate();
  
  const { getPatientDetails, submitConsentForm, loading: patientsLoading } = usePatients();
  const sigPadRef = useRef<SignatureCanvas>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  const location = useLocation();
  const { patientId } = location.state || {};
  const [patientDetail, setPatientDetails] = useState<PatientDetails>();

  const [formData, setFormData] = useState({
      name: "",
      highBloodPressure: "",
      heartAttack: "",
      heartDisease: "",
      pad: "",
      dvt: "",
      pacemaker: "",
      raynauds: "",
      coldUrticaria: "",
      cryoglobulinemia: "",
      respiratoryDisease: "",
      epilepsy: "",
      anaemia: "",
      openWounds: "",
      infection: "",
      pregnancy: "",
      cancer: "",
      email: "",
      phone: "",
      dob: "",
      signature: "", 
      date: "",
      parent_or_guardian: "",
      patientId: patientId,
      therapyName: 'cryochamber'
  });

  useEffect(() => {
      if (!patientId) return;
      // Fetch patient details
      getPatientDetails(patientId, (res: any) => {
          setPatientDetails(res);
      });
  }, [patientId]);

  useEffect(() => {
    if (patientDetail) {
      setFormData((prev) => ({
        ...prev,
        name: patientDetail.name || '',
        email: patientDetail.email || '',
        phone: patientDetail.phone || '',
      }));
    }
  }, [patientDetail]);

  // Validation logic
  const validateForm = () => {
    let newErrors: any = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    // if (!formData.parent_or_guardian.trim()) newErrors.parent_or_guardian = "parent or guardian is required";
    if (!formData.highBloodPressure) newErrors.highBloodPressure = "Select Yes or No";
    if (!formData.heartAttack) newErrors.heartAttack = "Select Yes or No";
    if (!formData.heartDisease) newErrors.heartDisease = "Select Yes or No";
    if (!formData.pad) newErrors.pad = "Select Yes or No";
    if (!formData.dvt) newErrors.dvt = "Select Yes or No";
    if (!formData.pacemaker) newErrors.pacemaker = "Select Yes or No";
    if (!formData.raynauds) newErrors.raynauds = "Select Yes or No";
    if (!formData.coldUrticaria) newErrors.coldUrticaria = "Select Yes or No";
    if (!formData.cryoglobulinemia) newErrors.cryoglobulinemia = "Select Yes or No";
    if (!formData.respiratoryDisease) newErrors.respiratoryDisease = "Select Yes or No";
    if (!formData.epilepsy) newErrors.epilepsy = "Select Yes or No";
    if (!formData.anaemia) newErrors.anaemia = "Select Yes or No";
    if (!formData.openWounds) newErrors.openWounds = "Select Yes or No";
    if (!formData.infection) newErrors.infection = "Select Yes or No";
    if (!formData.pregnancy) newErrors.pregnancy = "Select Yes or No";
    if (!formData.cancer) newErrors.cancer = "Select Yes or No";
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.signature) newErrors.signature = "Signature is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveSignature = () => {
      if (!sigPadRef.current?.isEmpty()) {
          const dataURL = sigPadRef.current!.toDataURL("image/png");
          setFormData(prev => ({ ...prev, signature: dataURL }));
          setShowSignaturePad(false); // Hide pad after saving
      } else {
          alert("Please provide a signature before saving.");
      }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const clearSignature = () => {
      sigPadRef.current?.clear();
      setFormData((prev) => ({ ...prev, signature: "" }));
  };

  const handleSubmit = async (e: any) => {
      e.preventDefault();

      if (!validateForm()) {
        // alert("Please fill all required fields.");
        return;
      }

      if (submitting) return; // Prevent double click
      setSubmitting(true); // Start loading

        // Check if signature EXISTS in formData (not on canvas)
      if (!formData.signature) {
          alert("Please provide a signature.");
          setSubmitting(false);
          return;
      }

      // Now package everything
      const payload = {
      ...formData,
      patientId: patientId,
      };

      try {
        submitConsentForm(formData, (res) => {
          if (isPatientUser(appState?.userRole)) {
            navigateTo(PAGE_ROUTES.PATIENT.ConsentForm);
          } else {
            navigateTo(PAGE_ROUTES.ADMIN.PatientProfile, {
              state: { patientId: patientId, patientName: patientDetail.name }
            });
          }
          // navigateTo(isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.ConsentForm : PAGE_ROUTES.ADMIN.PatientProfile);
        });
      } catch (error) {
        console.error("Error submitting:", error);
      } finally {
        setSubmitting(false); // Stop loading after response
      }

  };

  return (
    <DashboardLayout breadcrumbItems={breadcrumbItems}>

      <Container className="my-4 p-0">
        <Card className="shadow-sm">
          <Card.Header className="bg-dark text-white">
            <h2 className="mb-0">CryoChamber Client Details</h2>
          </Card.Header>

          <Card.Body>
            <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Client Name:</strong></Form.Label>
                  <Form.Control type="text" placeholder="Enter full name"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.name} />
                    {errors.name && <div className="text-danger small">{errors.name}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Date of Birth:</strong></Form.Label>
                  <Form.Control type="date"
                  name="dob"
                  value={formData.dob || ''}
                  onChange={handleChange}
                  isInvalid={!!errors.dob} />
                  {errors.dob && <div className="text-danger small">{errors.dob}</div>}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Email:</strong></Form.Label>
                  <Form.Control type="email" placeholder="Enter email address"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.email} />
                  {errors.email && <div className="text-danger small">{errors.email}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Phone:</strong></Form.Label>
                  <Form.Control type="tel" placeholder="Enter phone number"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.phone} />
                  {errors.phone && <div className="text-danger small">{errors.phone}</div>}
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-4" />

            <div className="mb-4">
              <h4 className="text-danger mb-3">CONTRAINDICATIONS</h4>
              <Alert variant="warning" className="mb-4">
                <strong>Cryotherapy is NOT recommended</strong> if you have or have ever had any of the following:
              </Alert>

              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead className="table-dark">
                    <tr>
                      <th >Condition</th>
                      <th className="text-center">Yes</th>
                      <th className="text-center">No</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        Uncontrolled high blood pressure (≥180/100 mmHg)
                        {errors.highBloodPressure && <div className="text-danger small">{errors.highBloodPressure}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="highBloodPressure" value="yes"
                          checked={formData.highBloodPressure === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.highBloodPressure} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="highBloodPressure" value="no"
                          checked={formData.highBloodPressure === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.highBloodPressure} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Heart attack, stroke, or TIA within the last 6 months
                        {errors.heartAttack && <div className="text-danger small">{errors.heartAttack}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="heartAttack" value="yes"
                          checked={formData.heartAttack === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.heartAttack} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="heartAttack" value="no"
                          checked={formData.heartAttack === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.heartAttack} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Severe heart disease or unstable angina
                        {errors.heartDisease && <div className="text-danger small">{errors.heartDisease}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="heartDisease" value="yes"
                          checked={formData.heartDisease === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.heartDisease} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="heartDisease" value="no"
                          checked={formData.heartDisease === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.heartDisease} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Severe peripheral arterial disease (PAD)
                        {errors.pad && <div className="text-danger small">{errors.pad}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="pad" value="yes"
                          checked={formData.pad === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.pad} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="pad" value="no"
                          checked={formData.pad === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.pad} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Deep vein thrombosis (DVT) or pulmonary embolism
                        {errors.dvt && <div className="text-danger small">{errors.dvt}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="dvt" value="yes"
                          checked={formData.dvt === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.dvt} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="dvt" value="no"
                          checked={formData.dvt === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.dvt} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Pacemaker or cardiac defibrillator
                        {errors.pacemaker && <div className="text-danger small">{errors.pacemaker}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="pacemaker" value="yes"
                          checked={formData.pacemaker === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.pacemaker} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="pacemaker" value="no"
                          checked={formData.pacemaker === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.pacemaker} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Raynaud's disease (severe)
                        {errors.raynauds && <div className="text-danger small">{errors.raynauds}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="raynauds" value="yes"
                          checked={formData.raynauds === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.raynauds} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="raynauds" value="no"
                          checked={formData.raynauds === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.raynauds} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Cold urticaria (allergic reaction to cold)
                        {errors.coldUrticaria && <div className="text-danger small">{errors.coldUrticaria}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="coldUrticaria" value="yes"
                          checked={formData.coldUrticaria === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.coldUrticaria} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="coldUrticaria" value="no"
                          checked={formData.coldUrticaria === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.coldUrticaria} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Cryoglobulinemia or paroxysmal cold haemoglobinuria
                        {errors.cryoglobulinemia && <div className="text-danger small">{errors.cryoglobulinemia}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="cryoglobulinemia" value="yes"
                          checked={formData.cryoglobulinemia === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.cryoglobulinemia} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="cryoglobulinemia" value="no"
                          checked={formData.cryoglobulinemia === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.cryoglobulinemia} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Severe respiratory disease (e.g. uncontrolled COPD)
                        {errors.respiratoryDisease && <div className="text-danger small">{errors.respiratoryDisease}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="respiratoryDisease" value="yes"
                          checked={formData.respiratoryDisease === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.respiratoryDisease} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="respiratoryDisease" value="no"
                          checked={formData.respiratoryDisease === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.respiratoryDisease} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Uncontrolled seizures or epilepsy
                        {errors.epilepsy && <div className="text-danger small">{errors.epilepsy}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="epilepsy" value="yes"
                          checked={formData.epilepsy === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.epilepsy} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="epilepsy" value="no"
                          checked={formData.epilepsy === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.epilepsy} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Severe anaemia
                        {errors.anaemia && <div className="text-danger small">{errors.anaemia}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="anaemia" value="yes"
                          checked={formData.anaemia === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.anaemia} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="anaemia" value="no"
                          checked={formData.anaemia === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.anaemia} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Open wounds, severe burns, or unhealed injuries
                        {errors.openWounds && <div className="text-danger small">{errors.openWounds}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="openWounds" value="yes"
                          checked={formData.openWounds === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.openWounds} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="openWounds" value="no"
                          checked={formData.openWounds === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.openWounds} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Active infections or fever
                        {errors.infection && <div className="text-danger small">{errors.infection}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="infection" value="yes"
                          checked={formData.infection === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.infection} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="infection" value="no"
                          checked={formData.infection === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.infection} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Pregnancy
                        {errors.pregnancy && <div className="text-danger small">{errors.pregnancy}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="pregnancy" value="yes"
                          checked={formData.pregnancy === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.pregnancy} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="pregnancy" value="no"
                          checked={formData.pregnancy === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.pregnancy} />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Cancer (unless cleared by a doctor)
                        {errors.cancer && <div className="text-danger small">{errors.cancer}</div>}
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="cancer" value="yes"
                          checked={formData.cancer === "yes"}
                          onChange={handleChange}
                          isInvalid={!!errors.cancer} />
                      </td>
                      <td className="text-center">
                        <Form.Check type="radio" name="cancer" value="no"
                          checked={formData.cancer === "no"}
                          onChange={handleChange}
                          isInvalid={!!errors.cancer} />
                      </td>
                    </tr>
                  </tbody>

                </Table>
              </div>
            </div>

            <Alert variant="danger" className="mb-3">
              <strong>Important:</strong> If the client answers Yes to any of the above please discuss and clarify before allowing the client to enter the chamber. If appropriate or any doubt refer the client to their GP.
            </Alert>

            <Alert variant="warning">
              <strong>Chamber technician:</strong> If any of the above questions are answered YES then the client may NOT be admitted to the Cryo Chamber without consent from their GP. If yes is answered to any question in bold then the client may not use the chamber at any time. If the client has piercings then we recommend removing them. If the client cannot remove them then ensure that the piercing(s) is covered by vest, shorts, headband, etc. depending on piercing location. Eyebrow piercings must be removed.
            </Alert>

            <div className="mb-4">
              <h4 className="fw-bold mb-3" style={{ fontSize: '18px' }}>PRECAUTIONARY CONDITIONS</h4>
              <p className="mb-3" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                Cryotherapy may still be possible but requires medical clearance or a modified protocol if you have:
              </p>

              <Row className="mb-4">
                <Col md={6}>
                  <ListGroup variant="flush" className="mb-3" style={{ fontSize: '14px' }}>
                    <ListGroup.Item className="px-0 py-1 border-0">• controlled hypertension</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• diabetes with neuropathy</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• asthma or respiratory conditions</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• chronic kidney or liver disease</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• recent surgery or injury</ListGroup.Item>
                  </ListGroup>
                </Col>
                <Col md={6}>
                  <ListGroup variant="flush" className="mb-3" style={{ fontSize: '14px' }}>
                    <ListGroup.Item className="px-0 py-1 border-0">• metal implants</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• severe migraines</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• claustrophobia</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• recent alcohol or drug use</ListGroup.Item>
                    <ListGroup.Item className="px-0 py-1 border-0">• if you are elderly with reduced circulation.</ListGroup.Item>
                  </ListGroup>
                </Col>
              </Row>

              <h5 className="fw-bold mb-3 mt-4" style={{ fontSize: '16px', textDecoration: 'underline' }}>
                SAFETY INSTRUCTIONS AND UNDERLINED
              </h5>

              <div className="mb-3" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                <div className="mb-2">• I understand that it is mandatory to wear personal protective equipment (PPE) consisting of a headband mask, gloves, socks, face mask, enclosed footwear, shorts and vest during my whole body cryotherapy (WBC) session as a safety precaution.</div>
                <div className="mb-2">• I also understand that I should not remove personal protective equipment (PPE) at any time during my whole body cryotherapy (WBC) session</div>
                <div className="mb-2">• I understand that wet or damp clothing cannot be worn at any time during a whole body cryotherapy (WBC) session. If you need dry clothing; it is available upon request; please ask the receptionist or chamber technician for assistance</div>
                <div className="mb-2">• I understand that I cannot enter the chamber with wet hair or moisture or oil on my skin</div>
                <div className="mb-2">• metal implants</div>
                <div className="mb-2">• severe migraines</div>
                <div className="mb-2">• claustrophobia</div>
                <div className="mb-2">• recent alcohol or drug use</div>
                <div className="mb-2">• if you are elderly with reduced circulation.</div>
              </div>

              <div className="mb-3" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                <div className="mb-2">• I confirm I have had the opportunity to consider the information, ask questions and have had these answered satisfactorily</div>
                <div className="mb-2">• I understand that users of the cryo chamber must be at least 16 years of age. Alternatively if a client is under 16 years old treatment may be given if a parent or guardian countersigns the consent form</div>
                <div className="mb-2">• I understand that if I wear contact lenses, it may be helpful to close or blink my eyes to moisturise them</div>
                <div className="mb-2">• I understand that I may be refused entry to the cryo chamber for safety reasons.</div>
                <div className="mb-2">• I confirm that I have read and understood all of the information in this form.</div>
                <div className="mb-2">• Any change in medical condition between treatments should be declared</div>
              </div>
            </div>

            <hr className="my-4" />

            <div className="mb-4">
              <h4 className="fw-bold mb-3" style={{ fontSize: '18px' }}>CLIENT RESPONSIBILITIES</h4>
              <p className="mb-3" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                By signing this form, you agree to follow safety instructions, wear required protective gear, notify staff of any discomfort, and inform us of any new medical diagnoses before future sessions.
              </p>
            </div>

            <div className="mb-4">
              <h4 className="fw-bold mb-3" style={{ fontSize: '18px' }}>INFORMED CONSENT & LIABILITY WAIVER</h4>
              <p className="mb-4" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                I confirm that I have read and understood the information above, fully disclosed my medical history, had the opportunity to ask questions, and understand the potential risks. I voluntarily consent to undergo cryotherapy and release Heal Wellness Clinic, its practitioners, and staff from liability for any adverse effects, provided all safety protocols are followed.
              </p>

              <Row className="mt-4">
                <Col md={6}>
                  <div className="mb-3">
                      <Form.Group className="">
                        <Form.Label className="fw-bold">Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter name"
                          className="border-dark"
                          name="name"
                          value={formData.name || ''}
                          onChange={handleChange}
                          isInvalid={!!errors.name}
                        />
                        {errors.name && <div className="text-danger small">{errors.name}</div>}
                      </Form.Group>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                      <Form.Label><strong>Date:</strong></Form.Label>
                      <Form.Control type="date" placeholder="Enter date"
                        name="date"
                        value={formData.date || ''}
                        onChange={handleChange}
                        isInvalid={!!errors.date} />
                        {errors.date && <div className="text-danger small">{errors.date}</div>}
                      </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={8}>
                  <div className="mb-3">
                    {/* <div style={{ fontSize: '14px', marginBottom: '5px' }}>Signature:..........................................................................................</div> */}
                    <div>
                      <label className="fw-bold me-2">Signature</label>

                      {!formData.signature && !showSignaturePad && (
                      <Button type="button" onClick={() => setShowSignaturePad(true)}>
                          Add Signature
                      </Button>
                      )}

                      {showSignaturePad && (
                      <div style={{ border: "1px solid #000", padding: "10px" }}>
                          <SignatureCanvas
                          ref={sigPadRef}
                          penColor="black"
                          canvasProps={{ width: 400, height: 200, className: "sigCanvas" }}
                          />
                          <div className="mt-2">
                          <Button type="button" onClick={handleSaveSignature} className="me-2">Save</Button>
                          <Button type="button" onClick={() => sigPadRef.current?.clear()}>
                              Clear
                          </Button>
                          </div>
                      </div>
                      )}

                      {formData.signature && !showSignaturePad && (
                      <div className="d-flex align-items-center gap-3 mt-2">
                          <img src={formData.signature} alt="Signature" width="200" height="80" style={{ border: "1px solid #ccc" }} />
                          <Button type="button" onClick={() => setShowSignaturePad(true)}>Edit</Button>
                      </div>
                      )}
                    </div>
                    {errors.signature && <div className="text-danger small">{errors.signature}</div>}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                      <Form.Label><strong>Parent or Guardian (if applicable):</strong></Form.Label>
                      <Form.Control type="text" placeholder=""
                        name="parent_or_guardian"
                        value={formData.parent_or_guardian || ''}
                        onChange={handleChange}
                        // isInvalid={!!errors.parent_or_guardian}
                        />
                        {/* {errors.parent_or_guardian && <div className="text-danger small">{errors.parent_or_guardian}</div>} */}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                      <Form.Label><strong>Date:</strong></Form.Label>
                      <Form.Control type="date" placeholder="Enter date"
                        name="date"
                        value={formData.date || ''}
                        onChange={handleChange}isInvalid={!!errors.date} />
                      {errors.date && <div className="text-danger small">{errors.date}</div>}
                    </div>
                  </div>
                </Col>
              </Row>
              </div>
              <div className="d-flex align-items-center gap-3 mt-2">
                  <Button 
                      className="m-1" type="submit">
                      Submit
                  </Button>
              </div>
              </Form>
          </Card.Body>
        </Card>
      </Container>
    </DashboardLayout>
  );
};

export default CryoChamberFormPage;